import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CreditCard, Plus, Upload, Download, Edit2, Trash2, RefreshCw, Search, ChevronUp, ChevronDown, ChevronRight, CalendarRange, Split } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import { useUserPreferences, PREF_FIN_CREDIT_CARDS } from '@hooks/useUserPreferences';
import type { Company } from '@/types/company';
import { formatCnpj } from '@/types/company';
import type {
  CreditCard as CreditCardModel,
  CreditCardInvoice,
  CreditCardTransaction,
  CreditCardInvoiceTransactionsResponse,
  CreditCardProjection,
  InvoiceUploadReview,
  InvoiceUploadConfirmItem,
  GroupScope,
  FinancialAllocationRequest,
} from '@/types/financial';
import {
  formatCurrencyBRL,
  formatDateBR,
  buildCategoryOptions,
  filterCategoryGroupsByMovement,
  activityTypeLabel,
  activityTypeBadgeColor,
  groupScopeLabel,
} from './financialUtils';
import { toCents } from './installmentCalc';
import { AllocationEditor } from './AllocationEditor';
import {
  allocationsValidationMessage,
  allocationSummary,
  linesFromAllocations,
  newAllocationLine,
  principalErpId,
  toAllocationPayload,
  type AllocationLine,
} from './allocationCalc';
import type { CategoryOptionGroup } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  SummaryCard,
  ModalShell,
  CategorySelect,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './financialShared';
import { InstallmentAmountFields } from './InstallmentAmountFields';
import { CreditCardProjectionMatrix } from './CreditCardProjectionMatrix';

interface CreditCardsPrefs extends Record<string, unknown> {
  company_id?: number;
  card_id?: number;
}

// ─── Modal de lançamento de cartão ──────────────────────────────

interface CardTransactionModalProps {
  transaction: CreditCardTransaction | null; // null = novo
  scope: GroupScope;
  categoryGroups: CategoryOptionGroup[];
  onSave: (data: {
    description: string;
    invoiceDueDate: string;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
    installments: number;
    /** null = sem rateio; lista = partes (mín. 2, soma = amount). */
    allocations: FinancialAllocationRequest[] | null;
  }) => Promise<void>;
  onClose: () => void;
}

const CardTransactionModal: React.FC<CardTransactionModalProps> = ({ transaction, scope, categoryGroups, onSave, onClose }) => {
  const isEdit = transaction != null;
  const [description, setDescription] = useState(transaction?.description ?? '');
  const [invoiceDueDate, setInvoiceDueDate] = useState(
    transaction?.invoiceDueDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [amount, setAmount] = useState(transaction != null ? String(Math.abs(transaction.amount)) : '');
  const [activityType, setActivityType] = useState<number>(transaction?.activityType ?? 0);
  const [accountingPlanErpId, setAccountingPlanErpId] = useState<number | null>(transaction?.accountingPlanErpId ?? null);
  const [installments, setInstallments] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rateio entre categorias — não no escopo "todas as parcelas" (cada parcela
  // tem as suas partes; o back também recusa).
  const canAllocate = scope !== 'group';
  const [allocate, setAllocate] = useState<boolean>(Boolean(transaction?.allocated) && canAllocate);
  const [allocationLines, setAllocationLines] = useState<AllocationLine[]>(() =>
    transaction?.allocated ? linesFromAllocations(transaction.allocations) : [],
  );
  const allocationGroups = useMemo(
    () => filterCategoryGroupsByMovement(categoryGroups, activityType === 1 ? 1 : 0),
    [categoryGroups, activityType],
  );
  const totalCents = toCents(amount) ?? 0;

  const toggleAllocate = (on: boolean) => {
    setAllocate(on);
    if (on && allocationLines.length === 0) {
      setAllocationLines([{ ...newAllocationLine(), accountingPlanErpId }, newAllocationLine()]);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const parsedAmount = parseFloat(amount.replace(',', '.'));
    if (!description.trim()) {
      setError('Informe a descrição');
      return;
    }
    if (!invoiceDueDate) {
      setError('Informe a data');
      return;
    }
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Informe um valor positivo');
      return;
    }
    const parsedInstallments = parseInt(installments, 10);
    if (!isEdit && (isNaN(parsedInstallments) || parsedInstallments < 1)) {
      setError('Parcelas deve ser no mínimo 1');
      return;
    }
    if (allocate) {
      const msg = allocationsValidationMessage(totalCents, allocationLines);
      if (msg) {
        setError(msg);
        return;
      }
    }
    try {
      setSaving(true);
      await onSave({
        description: description.trim(),
        invoiceDueDate,
        amount: parsedAmount,
        activityType,
        accountingPlanErpId: allocate ? principalErpId(allocationLines) : accountingPlanErpId,
        installments: isEdit ? 1 : parsedInstallments,
        allocations: allocate ? toAllocationPayload(allocationLines) : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Editar Lançamento da Fatura' : 'Novo Lançamento na Fatura'}
      icon={<CreditCard size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      {isEdit && transaction.transactionGroup && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-xs">
          Escopo da edição: <span className="font-semibold">{groupScopeLabel(scope)}</span>
        </div>
      )}
      <div>
        <label className={labelCls}>Descrição *</label>
        <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} autoFocus />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Data *</label>
          <DateInputBR value={invoiceDueDate} onChange={setInvoiceDueDate} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Tipo *</label>
          <select className={selectCls} value={activityType} onChange={(e) => setActivityType(Number(e.target.value))}>
            <option value={0}>Saída</option>
            <option value={1}>Entrada</option>
          </select>
        </div>
      </div>
      {/* Total, quantidade e valor da parcela sempre sincronizados — editar
          qualquer um recalcula os outros dois. O payload segue sendo o TOTAL. */}
      <InstallmentAmountFields
        amount={amount}
        onAmountChange={setAmount}
        installments={installments}
        onInstallmentsChange={setInstallments}
        showInstallments={!isEdit}
      />
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={labelCls.replace(' mb-1', '')}>Categoria</label>
          <label
            className={clsx('inline-flex items-center gap-1.5 text-xs', canAllocate ? 'text-indigo-700 cursor-pointer' : 'text-slate-400')}
            title={canAllocate ? undefined : 'Rateio é por parcela — edite uma parcela por vez'}
          >
            <input
              type="checkbox"
              className="rounded border-slate-300"
              checked={allocate}
              disabled={!canAllocate}
              onChange={(e) => toggleAllocate(e.target.checked)}
            />
            <Split size={12} /> Ratear entre categorias
          </label>
        </div>
        {allocate ? (
          <>
            <AllocationEditor
              totalCents={totalCents}
              categoryGroups={allocationGroups}
              lines={allocationLines}
              onChange={setAllocationLines}
            />
            {!isEdit && parseInt(installments, 10) > 1 && (
              <p className="text-xs text-slate-500 mt-1">
                O rateio será replicado proporcionalmente em cada parcela (uma por fatura).
              </p>
            )}
          </>
        ) : (
          <CategorySelect groups={categoryGroups} value={accountingPlanErpId} onChange={setAccountingPlanErpId} />
        )}
      </div>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Prompt de escopo (parcelas) ────────────────────────────────

const ScopePromptModal: React.FC<{
  action: 'edit' | 'delete';
  onChoose: (scope: GroupScope) => void;
  onClose: () => void;
}> = ({ action, onChoose, onClose }) => (
  <ModalShell
    title="Aplicar a todas as parcelas?"
    onClose={onClose}
    maxWidth="max-w-md"
    footer={
      <button type="button" onClick={onClose} className={secondaryBtnCls}>
        Cancelar
      </button>
    }
  >
    <p className="text-sm text-slate-600">
      Este lançamento faz parte de um grupo de parcelas. Deseja {action === 'edit' ? 'editar' : 'excluir'}{' '}
      somente este lançamento ou todas as parcelas do grupo?
    </p>
    <div className="flex flex-col gap-2">
      <button type="button" onClick={() => onChoose('single')} className={secondaryBtnCls + ' justify-center'}>
        {groupScopeLabel('single')}
      </button>
      <button
        type="button"
        onClick={() => onChoose('group')}
        className={clsx(
          'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
          action === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white',
        )}
      >
        {groupScopeLabel('group')}
      </button>
    </div>
  </ModalShell>
);

// ─── Painel de revisão da importação de fatura ──────────────────

interface ReviewItemState extends InvoiceUploadConfirmItem {
  selected: boolean;
}

const InvoiceUploadReviewPanel: React.FC<{
  review: InvoiceUploadReview;
  categoryGroups: CategoryOptionGroup[];
  confirming: boolean;
  onConfirm: (items: InvoiceUploadConfirmItem[]) => void;
  onCancel: () => void;
}> = ({ review, categoryGroups, confirming, onConfirm, onCancel }) => {
  const [newItems, setNewItems] = useState<ReviewItemState[]>(() =>
    review.new.map((item) => ({
      description: item.description,
      invoiceDueDate: item.invoiceDueDate,
      amount: item.amount,
      repeatIndex: item.repeatIndex,
      repeatTotal: item.repeatTotal,
      accountingPlanErpId: item.suggestedAccountingPlanErpId,
      selected: true,
    })),
  );

  const updateItem = (index: number, fields: Partial<ReviewItemState>) => {
    setNewItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...fields } : item)));
  };

  const selectedCount = newItems.filter((i) => i.selected).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Revisão da Importação</h3>
        <div className="flex gap-2">
          <button className={secondaryBtnCls} onClick={onCancel} disabled={confirming}>
            Cancelar
          </button>
          <button
            className={primaryBtnCls}
            disabled={confirming || selectedCount === 0}
            onClick={() =>
              onConfirm(
                newItems
                  .filter((i) => i.selected)
                  .map(({ selected: _selected, ...item }) => item),
              )
            }
          >
            {confirming ? <RefreshCw size={14} className="animate-spin" /> : null}
            Confirmar ({selectedCount})
          </button>
        </div>
      </div>

      {/* Novos */}
      <div>
        <h4 className="text-sm font-semibold text-emerald-700 mb-2">Novos ({review.new.length})</h4>
        {newItems.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum lançamento novo no arquivo.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-2 px-3 w-10" />
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600 w-72">Categoria</th>
                </tr>
              </thead>
              <tbody>
                {newItems.map((item, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        checked={item.selected}
                        onChange={(e) => updateItem(index, { selected: e.target.checked })}
                      />
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap text-slate-700">{formatDateBR(item.invoiceDueDate)}</td>
                    <td className="py-2 px-3 text-slate-700">
                      {item.description}
                      {item.repeatIndex != null && item.repeatTotal != null && (
                        <span className="ml-2 inline-flex text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                          {item.repeatIndex}/{item.repeatTotal}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap text-slate-700">{formatCurrencyBRL(item.amount)}</td>
                    <td className="py-2 px-3">
                      <CategorySelect
                        groups={categoryGroups}
                        value={item.accountingPlanErpId}
                        onChange={(erpId) => updateItem(index, { accountingPlanErpId: erpId })}
                        disabled={!item.selected}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Encontrados */}
      <div>
        <h4 className="text-sm font-semibold text-slate-600 mb-2">Encontrados ({review.found.length})</h4>
        {review.found.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum lançamento já existente.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full text-sm">
              <tbody>
                {review.found.map((item, index) => (
                  <tr key={index} className="border-b border-slate-100 text-slate-500">
                    <td className="py-2 px-3 whitespace-nowrap">{formatDateBR(item.invoiceDueDate)}</td>
                    <td className="py-2 px-3">{item.description}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">{formatCurrencyBRL(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ausentes no arquivo */}
      <div>
        <h4 className="text-sm font-semibold text-amber-700 mb-2">Ausentes no arquivo ({review.missingFromFile.length})</h4>
        {review.missingFromFile.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum lançamento da fatura ausente no arquivo.</p>
        ) : (
          <div className="overflow-x-auto border border-amber-200 rounded-lg bg-amber-50/50">
            <table className="min-w-full text-sm">
              <tbody>
                {review.missingFromFile.map((item, index) => (
                  <tr key={index} className="border-b border-amber-100 text-slate-600">
                    <td className="py-2 px-3 whitespace-nowrap">{formatDateBR(item.invoiceDueDate)}</td>
                    <td className="py-2 px-3">{item.description}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">{formatCurrencyBRL(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const CreditCardsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financial.credit_card.write').hasPermission;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [cards, setCards] = useState<CreditCardModel[]>([]);
  const [cardId, setCardId] = useState<number | null>(null);
  const [invoices, setInvoices] = useState<CreditCardInvoice[]>([]);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [invoiceData, setInvoiceData] = useState<CreditCardInvoiceTransactionsResponse | null>(null);
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Aba ativa: os lançamentos por fatura (tela original) ou a projeção de
  // parcelas por competência.
  const [activeTab, setActiveTab] = useState<'lancamentos' | 'projecao'>('lancamentos');
  const [projection, setProjection] = useState<CreditCardProjection | null>(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [projectionError, setProjectionError] = useState<string | null>(null);
  /** Cartão da visão da projeção; null = consolidado (todos, todas as empresas). */
  const [projectionCardId, setProjectionCardId] = useState<number | null>(null);
  const [txModal, setTxModal] = useState<{ transaction: CreditCardTransaction | null; scope: GroupScope } | null>(null);
  const [scopePrompt, setScopePrompt] = useState<{ action: 'edit' | 'delete'; transaction: CreditCardTransaction } | null>(null);
  /** Itens rateados com as partes abertas na tela. */
  const [expandedAllocations, setExpandedAllocations] = useState<Set<number>>(new Set());
  const toggleAllocations = (transactionId: number) =>
    setExpandedAllocations((prev) => {
      const next = new Set(prev);
      if (next.has(transactionId)) next.delete(transactionId);
      else next.add(transactionId);
      return next;
    });
  const [uploadReview, setUploadReview] = useState<InvoiceUploadReview | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Busca + ordenação (client-side, sobre a fatura já carregada). Atenção: a data do lançamento
  // de cartão é invoiceDueDate (não transactionDate).
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<'invoiceDueDate' | 'amount' | 'categoryLabel'>('invoiceDueDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: 'invoiceDueDate' | 'amount' | 'categoryLabel') => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: 'invoiceDueDate' | 'amount' | 'categoryLabel') => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <ChevronUp size={13} className="text-emerald-600" />
      : <ChevronDown size={13} className="text-emerald-600" />;
  };

  const visibleTransactions = useMemo(() => {
    const rows = invoiceData?.transactions ?? [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((t) =>
          (t.description ?? '').toLowerCase().includes(term) ||
          (t.categoryLabel ?? '').toLowerCase().includes(term) ||
          (t.invoiceDueDate ?? '').toLowerCase().includes(term) ||
          formatDateBR(t.invoiceDueDate).toLowerCase().includes(term),
        )
      : rows;

    const sorted = [...filtered].sort((a, b) => {
      let cmp: number;
      if (sortColumn === 'amount') {
        cmp = a.amount - b.amount;
      } else if (sortColumn === 'categoryLabel') {
        cmp = (a.categoryLabel ?? '').localeCompare(b.categoryLabel ?? '', 'pt-BR');
      } else {
        cmp = (a.invoiceDueDate ?? '').localeCompare(b.invoiceDueDate ?? '', 'pt-BR');
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [invoiceData, search, sortColumn, sortDirection]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<CreditCardsPrefs>(PREF_FIN_CREDIT_CARDS);
  const cardRestored = useRef(false);

  // Empresas — espera as preferências para não selecionar a primeira empresa antes da restaurada
  useEffect(() => {
    if (!prefsLoaded) return;
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length === 0) return;
        const saved = list.find((c) => c.company_id === prefs?.company_id);
        setCompanyId(saved?.company_id ?? list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  // Cartões + plano de contas
  useEffect(() => {
    if (companyId == null) return;
    setCards([]);
    setCardId(null);
    setInvoices([]);
    setInvoiceId(null);
    setInvoiceData(null);
    financialService
      .getCreditCards(companyId)
      .then((list) => {
        setCards(list);
        if (list.length === 0) return;
        // Restaura o cartão salvo apenas na primeira carga; ao trocar de empresa vale o primeiro cartão dela.
        const saved = cardRestored.current
          ? undefined
          : list.find((c) => c.idCreditCards === prefs?.card_id);
        cardRestored.current = true;
        setCardId(saved?.idCreditCards ?? list[0].idCreditCards);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar cartões'));
    financialService
      .getAccountingPlans(companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Persiste os filtros (a fatura selecionada é o período e não é persistida)
  useEffect(() => {
    if (!prefsLoaded || companyId == null) return;
    savePrefs({ company_id: companyId, card_id: cardId ?? undefined });
  }, [prefsLoaded, companyId, cardId, savePrefs]);

  // Faturas do cartão
  useEffect(() => {
    if (cardId == null) return;
    setInvoices([]);
    setInvoiceId(null);
    setInvoiceData(null);
    financialService
      .getCreditCardInvoices(cardId)
      .then((result) => {
        setInvoices(result.invoices);
        const defaultId =
          result.currentInvoiceId != null && result.invoices.some((i) => i.idCreditCardsInvoices === result.currentInvoiceId)
            ? result.currentInvoiceId
            : result.invoices[0]?.idCreditCardsInvoices ?? null;
        setInvoiceId(defaultId);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar faturas'));
  }, [cardId]);

  // Lançamentos da fatura
  const loadInvoiceTransactions = useCallback(async () => {
    if (invoiceId == null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await financialService.getInvoiceTransactions(invoiceId);
      setInvoiceData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos da fatura');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    loadInvoiceTransactions();
  }, [loadInvoiceTransactions]);

  const fetchProjection = useCallback(async () => {
    setProjectionLoading(true);
    setProjectionError(null);
    try {
      setProjection(await financialService.getCreditCardProjection());
    } catch (err) {
      setProjectionError(err instanceof Error ? err.message : 'Erro ao carregar a projeção');
      setProjection(null);
    } finally {
      setProjectionLoading(false);
    }
  }, []);

  // Carrega a projeção só quando a aba é ativada (uma vez por visita).
  useEffect(() => {
    if (activeTab === 'projecao' && projection === null && !projectionLoading && !projectionError) {
      fetchProjection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchProjection]);

  // ── Ações ───────────────────────────────────────────────────

  const handleSaveTransaction = async (data: {
    description: string;
    invoiceDueDate: string;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
    installments: number;
    allocations: FinancialAllocationRequest[] | null;
  }) => {
    if (invoiceId == null) throw new Error('Selecione uma fatura');
    if (txModal?.transaction) {
      await financialService.updateCreditCardTransaction(txModal.transaction.transactionId, txModal.scope, {
        description: data.description,
        invoiceDueDate: data.invoiceDueDate,
        amount: data.amount,
        activityType: data.activityType,
        accountingPlanErpId: data.accountingPlanErpId,
        // Tri-state do PUT: [] remove o rateio; a tela sempre é explícita.
        allocations: data.allocations ?? [],
      });
      setNotice('Lançamento atualizado com sucesso');
    } else {
      await financialService.createCreditCardTransaction({
        idCreditCardsInvoices: invoiceId,
        description: data.description,
        invoiceDueDate: data.invoiceDueDate,
        amount: data.amount,
        activityType: data.activityType,
        accountingPlanErpId: data.accountingPlanErpId,
        ...(data.installments > 1 ? { installments: data.installments } : {}),
        ...(data.allocations ? { allocations: data.allocations } : {}),
      });
      setNotice('Lançamento criado com sucesso');
    }
    await loadInvoiceTransactions();
  };

  const handleEditClick = (transaction: CreditCardTransaction) => {
    // Parcela rateada é editada uma a uma (o back recusa o escopo grupo).
    if (transaction.transactionGroup && !transaction.allocated) {
      setScopePrompt({ action: 'edit', transaction });
    } else {
      setTxModal({ transaction, scope: 'single' });
    }
  };

  const performDelete = async (transaction: CreditCardTransaction, scope: GroupScope) => {
    setBusy(true);
    setError(null);
    try {
      await financialService.deleteCreditCardTransaction(transaction.transactionId, scope);
      setNotice('Lançamento excluído com sucesso');
      await loadInvoiceTransactions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir lançamento');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteClick = (transaction: CreditCardTransaction) => {
    if (transaction.transactionGroup) {
      setScopePrompt({ action: 'delete', transaction });
    } else if (
      window.confirm(
        `Excluir o lançamento "${transaction.description}"?` +
          // Mesma regra da Conta Corrente: excluir o mês de uma recorrência é
          // definitivo, o processo diário passa a pular essa fatura.
          (transaction.recurringTransactionId != null
            ? '\n\nEsta fatura não será recriada pelo processo diário de recorrências.'
            : '')
      )
    ) {
      performDelete(transaction, 'single');
    }
  };

  const handleScopeChosen = (scope: GroupScope) => {
    if (!scopePrompt) return;
    const { action, transaction } = scopePrompt;
    setScopePrompt(null);
    if (action === 'edit') {
      setTxModal({ transaction, scope });
    } else {
      performDelete(transaction, scope);
    }
  };

  const handleInvoiceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || invoiceId == null) return;
    setBusy(true);
    setError(null);
    try {
      const review = await financialService.uploadInvoiceFile(invoiceId, file);
      setUploadReview(review);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar arquivo da fatura');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmUpload = async (items: InvoiceUploadConfirmItem[]) => {
    if (invoiceId == null) return;
    setConfirming(true);
    setError(null);
    try {
      const result = await financialService.confirmInvoiceUpload(invoiceId, items);
      const warningSuffix = result.warnings.length > 0 ? ` — Avisos: ${result.warnings.join('; ')}` : '';
      setNotice(`Importação concluída — ${result.insertedCount} lançamento(s) inserido(s)${warningSuffix}`);
      setUploadReview(null);
      await loadInvoiceTransactions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao confirmar importação');
    } finally {
      setConfirming(false);
    }
  };

  const handleExportCsv = async () => {
    if (invoiceId == null) return;
    setBusy(true);
    setError(null);
    try {
      await financialService.exportInvoiceCsv(invoiceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar CSV');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <PermissionGuard permission="financial.credit_card.read" fallback={<PermissionFallback label="os cartões de crédito" />}>
      <div className="space-y-6">
        {/* Modais */}
        {txModal && (
          <CardTransactionModal
            transaction={txModal.transaction}
            scope={txModal.scope}
            categoryGroups={categoryGroups}
            onSave={handleSaveTransaction}
            onClose={() => setTxModal(null)}
          />
        )}
        {scopePrompt && (
          <ScopePromptModal action={scopePrompt.action} onChoose={handleScopeChosen} onClose={() => setScopePrompt(null)} />
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Cartões de Crédito</h1>
            <p className="text-sm text-slate-500 mt-1">Faturas e lançamentos dos cartões de crédito</p>
          </div>
          {activeTab === 'lancamentos' && (
          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <>
                <button
                  className={primaryBtnCls}
                  onClick={() => setTxModal({ transaction: null, scope: 'single' })}
                  disabled={invoiceId == null}
                >
                  <Plus size={16} /> Novo Lançamento
                </button>
                <button
                  className={secondaryBtnCls}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || invoiceId == null}
                >
                  <Upload size={16} /> Importar Fatura
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv,.txt,.xlsx,.pdf,.ofx"
                  onChange={handleInvoiceFile}
                />
              </>
            )}
            <button className={secondaryBtnCls} onClick={handleExportCsv} disabled={busy || invoiceId == null}>
              <Download size={16} /> Exportar CSV
            </button>
          </div>
          )}
        </div>

        {/* Abas */}
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px gap-0 overflow-x-auto" aria-label="Abas de cartões de crédito">
            {(
              [
                { id: 'lancamentos', label: 'Lançamentos', icon: <CreditCard size={16} /> },
                { id: 'projecao', label: 'Projeção', icon: <CalendarRange size={16} /> },
              ] as const
            ).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'inline-flex items-center gap-2 whitespace-nowrap py-3 px-5 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'lancamentos' && (
        <>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Cartão</label>
              <select
                className={selectCls + ' w-64'}
                value={cardId ?? ''}
                onChange={(e) => setCardId(e.target.value ? Number(e.target.value) : null)}
              >
                {cards.length === 0 && <option value="">Nenhum cartão disponível</option>}
                {cards.map((card) => (
                  <option key={card.idCreditCards} value={card.idCreditCards}>
                    {card.description}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Fatura</label>
              <select
                className={selectCls + ' w-64'}
                value={invoiceId ?? ''}
                onChange={(e) => setInvoiceId(e.target.value ? Number(e.target.value) : null)}
              >
                {invoices.length === 0 && <option value="">Nenhuma fatura disponível</option>}
                {invoices.map((invoice) => (
                  <option key={invoice.idCreditCardsInvoices} value={invoice.idCreditCardsInvoices}>
                    {invoice.description || `${String(invoice.month).padStart(2, '0')}/${invoice.year}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} onRetry={loadInvoiceTransactions} />}

        {/* Painel de revisão da importação */}
        {uploadReview && (
          <InvoiceUploadReviewPanel
            review={uploadReview}
            categoryGroups={categoryGroups}
            confirming={confirming}
            onConfirm={handleConfirmUpload}
            onCancel={() => setUploadReview(null)}
          />
        )}

        {loading && <LoadingSpinner />}

        {!loading && invoiceData && (
          <>
            {/* Total da fatura */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SummaryCard label="Total da Fatura" value={formatCurrencyBRL(invoiceData.total)} valueClassName="text-red-600" />
            </div>

            {/* Busca */}
            <div className="relative max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className={inputCls + ' pl-9'}
                placeholder="Buscar por descrição, categoria ou data..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Tabela */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th
                        className="text-left py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('invoiceDueDate')}
                      >
                        <span className="inline-flex items-center gap-1">Data{renderSortIcon('invoiceDueDate')}</span>
                      </th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                      <th
                        className="text-left py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('categoryLabel')}
                      >
                        <span className="inline-flex items-center gap-1">Categoria{renderSortIcon('categoryLabel')}</span>
                      </th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600">Tipo</th>
                      <th
                        className="text-right py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('amount')}
                      >
                        <span className="inline-flex items-center justify-end gap-1">Valor{renderSortIcon('amount')}</span>
                      </th>
                      {canWrite && <th className="text-right py-2 px-3 font-semibold text-slate-600 w-24">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTransactions.length === 0 && (
                      <tr>
                        <td colSpan={canWrite ? 6 : 5} className="py-10 text-center text-slate-400">
                          {invoiceData.transactions.length === 0
                            ? 'Nenhum lançamento nesta fatura'
                            : 'Nenhum lançamento corresponde à busca'}
                        </td>
                      </tr>
                    )}
                    {visibleTransactions.map((t) => (
                      <React.Fragment key={t.transactionId}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatDateBR(t.invoiceDueDate)}</td>
                        <td className="py-2 px-3 text-slate-700">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{t.description}</span>
                            {t.repeatIndex != null && t.repeatTotal != null && (
                              <span className="inline-flex text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-medium">
                                {t.repeatIndex}/{t.repeatTotal}
                              </span>
                            )}
                            {/* Rateado entre categorias: abrir mostra as partes. */}
                            {t.allocated && (
                              <button
                                onClick={() => toggleAllocations(t.transactionId)}
                                className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 font-medium hover:bg-indigo-100"
                                title={allocationSummary(t.allocations ?? [])}
                              >
                                {expandedAllocations.has(t.transactionId) ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                <Split size={11} /> Rateado · {t.allocations?.length ?? 0}
                              </button>
                            )}
                            {/* Assinatura recorrente: o valor da definição é a
                                estimativa; o real chega na fatura e é ajustado
                                aqui. O badge diz qual dos dois está na tela. */}
                            {t.recurringTransactionId != null && !t.recurringLocked && (
                              <span
                                className="inline-flex text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-medium"
                                title="Gerado da recorrência — o valor ainda é atualizado pela definição do lançamento recorrente"
                              >
                                Recorrente
                              </span>
                            )}
                            {t.recurringLocked && (
                              <span
                                className="inline-flex text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium"
                                title="Ajustado manualmente — o processo diário não altera mais este lançamento"
                              >
                                Recorrente · ajustado
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-slate-500">
                          {t.categoryLabel ?? '—'}
                          {t.allocated && (t.allocations?.length ?? 0) > 1 && (
                            <span className="ml-1 text-xs text-indigo-600" title="Categoria principal; as demais partes estão no rateio">
                              +{(t.allocations?.length ?? 1) - 1}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={clsx('inline-flex text-xs rounded-full px-2 py-0.5 font-medium', activityTypeBadgeColor(t.activityType))}>
                            {activityTypeLabel(t.activityType)}
                          </span>
                        </td>
                        <td className={clsx('py-2 px-3 text-right font-medium whitespace-nowrap', t.amount < 0 ? 'text-red-600' : 'text-emerald-600')}>
                          {formatCurrencyBRL(t.amount)}
                        </td>
                        {canWrite && (
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                                title="Editar"
                                onClick={() => handleEditClick(t)}
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                                title="Excluir"
                                onClick={() => handleDeleteClick(t)}
                                disabled={busy}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {t.allocated && expandedAllocations.has(t.transactionId) && (
                        <tr className="bg-indigo-50/60 border-b border-indigo-100">
                          <td colSpan={canWrite ? 6 : 5} className="px-6 py-3">
                            <div className="text-xs font-medium text-indigo-800 mb-2">
                              Rateio de {formatCurrencyBRL(t.amount)} entre {t.allocations?.length ?? 0} categorias
                            </div>
                            <table className="w-full text-xs">
                              <tbody>
                                {(t.allocations ?? []).map((a) => (
                                  <tr key={a.allocationId} className="border-t border-indigo-100/70">
                                    <td className="py-1 pr-3 text-slate-700">{a.categoryLabel ?? `Categoria ${a.accountingPlanErpId}`}</td>
                                    <td className="py-1 pr-3 text-slate-500">{a.note ?? ''}</td>
                                    <td className={clsx('py-1 text-right font-medium whitespace-nowrap', a.amount < 0 ? 'text-red-600' : 'text-emerald-600')}>
                                      {formatCurrencyBRL(a.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        </>
        )}

        {activeTab === 'projecao' && (
        <>
          {/* Visão: consolidado (todos os cartões, todas as empresas) ou um cartão */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className={labelCls}>Visão</label>
                <select
                  className={`${selectCls} w-96`}
                  value={projectionCardId ?? ''}
                  onChange={e => setProjectionCardId(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <option value="">Consolidado — todos os cartões, todas as empresas</option>
                  {(projection?.cards ?? []).map(card => (
                    <option key={card.idCreditCards} value={card.idCreditCards}>
                      {(companies.find(c => c.company_id === card.companyId)?.name ?? `Empresa ${card.companyId ?? '?'}`) + ' — ' + card.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {projectionError && <ErrorBox message={projectionError} onRetry={fetchProjection} />}
          {projectionLoading && <LoadingSpinner />}
          {!projectionLoading && !projectionError && projection && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <h3 className="text-base font-semibold text-slate-800 mb-3">
                Parcelas por competência
                <span className="ml-2 text-xs font-normal text-slate-400">
                  o total do mês é a prévia da fatura
                </span>
              </h3>
              <CreditCardProjectionMatrix
                data={projection}
                companies={companies}
                view={{ kind: 'card', cardId: projectionCardId }}
              />
            </div>
          )}
        </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default CreditCardsView;
